import React, { useEffect, useState } from "react";
import { Flame, RefreshCw } from "lucide-react";
import { GOLD } from "../constants/theme";
import { VideoThumb } from "./VideoThumb";
import { supabase } from "../../supabaseClient";
import { useAdmin } from "../shared/AdminContext";

// Compact dashboard card — shows today's featured drill. Admins in admin
// mode can re-roll it (rotate) if the random pick feels off.
export function DrillOfDay({ C, dark, onOpen }) {
  const [drill, setDrill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const { isAdmin, adminMode } = useAdmin();

  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    setLoading(true);
    const { data: dodRow } = await supabase
      .from("drill_of_day")
      .select("drill_id")
      .eq("for_date", today)
      .maybeSingle();
    const drillId = dodRow?.drill_id;
    if (!drillId) {
      // Nothing scheduled — trigger a rotation client-side via RPC.
      await supabase.rpc("rotate_drill_of_day", { p_for_date: today });
      const { data: retry } = await supabase
        .from("drill_of_day")
        .select("drill_id")
        .eq("for_date", today)
        .maybeSingle();
      if (!retry?.drill_id) { setDrill(null); setLoading(false); return; }
      const { data: d } = await supabase
        .from("drills").select("id, title, category, duration, mux_playback_id, section")
        .eq("id", retry.drill_id).maybeSingle();
      setDrill(d || null);
    } else {
      const { data: d } = await supabase
        .from("drills").select("id, title, category, duration, mux_playback_id, section")
        .eq("id", drillId).maybeSingle();
      setDrill(d || null);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [today]);

  const reroll = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Clear today's row so rotation picks a fresh one.
      await supabase.from("drill_of_day").delete().eq("for_date", today);
      await supabase.rpc("rotate_drill_of_day", { p_for_date: today });
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={{padding:"14px 16px",borderRadius:12,background:C.cardGradient,border:`1px solid ${C.cardBorder}`,color:C.textDim,fontSize:12,display:"flex",alignItems:"center",gap:8}}>
        <Flame size={13} color={GOLD} style={{animation:"dodPulse 1.1s ease-in-out infinite"}}/>
        <style>{`@keyframes dodPulse { 0%,100% { opacity:.45; } 50% { opacity:1; } }`}</style>
        Loading drill of the day…
      </div>
    );
  }

  if (!drill) {
    return (
      <div style={{padding:"14px 16px",borderRadius:12,background:C.cardGradient,border:`1px solid ${C.cardBorder}`,color:C.textDim,fontSize:12}}>
        No drill of the day yet.
      </div>
    );
  }

  const dur = drill.duration
    ? (typeof drill.duration === "number" ? `${Math.floor(drill.duration/60)}:${String(drill.duration%60).padStart(2,"0")}` : drill.duration)
    : null;

  // A media row: the frame, then what it is. On its own in the side column
  // (no set of the day for this coach) it stands up instead, frame on top,
  // so it fills the height beside the workout rather than floating in it.
  return (
    <div onClick={()=>onOpen && onOpen(drill)}
      className="btn bam-thumb-card bam-dotd"
      style={{position:"relative",overflow:"hidden",borderRadius:14,flex:1,boxSizing:"border-box",
        background:C.cardGradient,border:`1px solid ${C.cardBorder}`,
        display:"flex",alignItems:"center",gap:14,padding:12,cursor:"pointer"}}>
      <div className="bam-dotd-art" style={{position:"relative",flexShrink:0,width:132,aspectRatio:"16/9",borderRadius:10,overflow:"hidden",background:"#000"}}>
        <VideoThumb playbackId={drill.mux_playback_id} width={480} height={270}/>
      </div>
      <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",gap:4}}>
        <div style={{fontSize:10.5,fontWeight:800,color:C.textMid,letterSpacing:1.3,textTransform:"uppercase"}}>Drill of the day</div>
        <div style={{fontSize:15,fontWeight:800,color:C.text,lineHeight:1.25,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{drill.title}</div>
        <div style={{fontSize:12,color:C.textMid}}>{[drill.category || "General", dur].filter(Boolean).join(" · ")}</div>
      </div>
      {isAdmin && adminMode && (
        <button onClick={(e)=>{e.stopPropagation();reroll();}} disabled={busy}
          title="Re-roll today's drill"
          style={{position:"absolute",top:8,right:8,padding:6,borderRadius:8,border:`1px solid ${C.border}`,background:C.bgCard,cursor:busy?"default":"pointer",color:C.textMid,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <RefreshCw size={12} style={{animation:busy?"spin 1s linear infinite":"none"}}/>
        </button>
      )}
    </div>
  );
}

export default DrillOfDay;
